import { io } from 'socket.io-client';

const getSocketUrl = () => {
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
  return apiUrl.replace('/api', '');
};

const SOCKET_URL = getSocketUrl();

class WebSocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
    this.eventHandlers = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
  }

  connect() {
    if (this.socket && this.connected) {
      return this.socket;
    }

    console.log('Connecting to WebSocket:', SOCKET_URL);

    this.socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts,
      timeout: 20000,
      path: '/socket.io/',
      forceNew: true,
      autoConnect: true
    });

    this.socket.on('connect', () => {
      console.log('WebSocket connected:', this.socket.id);
      this.connected = true;
      this.reconnectAttempts = 0;
      this.triggerEvent('connected');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason);
      this.connected = false;
      this.triggerEvent('disconnected', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message);
      this.reconnectAttempts++;
      this.triggerEvent('error', error);
    });

    return this.socket;
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  // Job-related events
  joinJob(jobId) {
    if (this.socket) {
      this.socket.emit('join-job', jobId);
    }
  }

  leaveJob(jobId) {
    if (this.socket) {
      this.socket.emit('leave-job', jobId);
    }
  }

  // Topic message streaming
  subscribeTopic(clusterId, topicName) {
    if (this.socket) {
      this.socket.emit('subscribe-topic', { clusterId, topicName });
    }
  }

  unsubscribeTopic(clusterId, topicName) {
    if (this.socket) {
      this.socket.emit('unsubscribe-topic', { clusterId, topicName });
    }
  }

  onTopicMessage(callback) {
    if (this.socket) {
      this.socket.on('topic-message', callback);
    }
  }

  // SSH Terminal - Real-time PTY
  sshConnect(host, username, password, privateKey, cols = 120, rows = 40) {
    if (this.socket) {
      this.socket.emit('ssh-connect', { host, username, password, privateKey, cols, rows });
    }
  }

  sshInput(input) {
    if (this.socket) {
      this.socket.emit('ssh-input', { input });
    }
  }

  sshResize(cols, rows) {
    if (this.socket) {
      this.socket.emit('ssh-resize', { cols, rows });
    }
  }

  sshDisconnect() {
    if (this.socket) {
      this.socket.emit('ssh-disconnect');
    }
  }

  onSSHConnected(callback) {
    if (this.socket) {
      this.socket.on('ssh-connected', callback);
    }
  }

  onSSHData(callback) {
    if (this.socket) {
      this.socket.on('ssh-data', callback);
    }
  }

  onSSHClosed(callback) {
    if (this.socket) {
      this.socket.on('ssh-closed', callback);
    }
  }

  onSSHError(callback) {
    if (this.socket) {
      this.socket.on('ssh-error', callback);
    }
  }

  // Job event handlers
  onLog(callback) {
    if (this.socket) this.socket.on('log', callback);
  }

  onProgress(callback) {
    if (this.socket) this.socket.on('progress', callback);
  }

  onStateChange(callback) {
    if (this.socket) this.socket.on('state-change', callback);
  }

  onJobComplete(callback) {
    if (this.socket) this.socket.on('job-complete', callback);
  }

  onJobFailed(callback) {
    if (this.socket) this.socket.on('job-failed', callback);
  }

  onJoinedJob(callback) {
    if (this.socket) this.socket.on('joined-job', callback);
  }

  off(eventName) {
    if (this.socket) this.socket.off(eventName);
  }

  on(event, handler) {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event).push(handler);
  }

  triggerEvent(event, data) {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }

  isConnected() {
    return this.connected;
  }

  getSocketId() {
    return this.socket?.id;
  }
}

const websocketService = new WebSocketService();
export default websocketService;
